import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { MapPin, Calculator, FileText, Wallet, CheckCircle, AlertTriangle, Info, Download, ArrowRight, Shield, TrendingUp, Globe, Heart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Mention légale obligatoire
const LEGAL_NOTICE = `Dispositif solidaire d'entraide et de prévention, fondé sur le droit civil français 
et les principes de l'Économie Sociale et Solidaire. 
Hors assurance – aucune prime – aucune garantie automatique.`;

export default function CoppamAdherentSpace() {
  const [territories, setTerritories] = useState([]);
  const [selectedTerritory, setSelectedTerritory] = useState('');
  const [territoryDetails, setTerritoryDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [step, setStep] = useState(1); // 1: choix territoire, 2: formulaire, 3: résultat
  
  // Formulaire simulation
  const [formData, setFormData] = useState({
    type_acteur: 'particulier',
    secteur: 'habitat',
    montant_projet: '',
    apport_personnel: '',
    duree_mois: '180',
    revenu_mensuel: ''
  });

  // Charger les territoires
  useEffect(() => {
    loadTerritories();
  }, []);

  const loadTerritories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/territories`);
      setTerritories(response.data.territories || []);
    } catch {
      toast.error('Erreur lors du chargement des territoires');
    }
  };

  const loadTerritoryDetails = async (code) => {
    try {
      const response = await axios.get(`${API_URL}/api/territories/${code}/parameters`);
      setTerritoryDetails(response.data);
    } catch {
    }
  };

  const handleTerritorySelect = (code) => {
    setSelectedTerritory(code);
    loadTerritoryDetails(code);
  };

  const handleSubmitSimulation = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        territory_code: selectedTerritory,
        type_acteur: formData.type_acteur,
        secteur: formData.secteur,
        montant_projet: parseFloat(formData.montant_projet) || 0,
        apport_personnel: parseFloat(formData.apport_personnel) || 0,
        duree_mois: parseInt(formData.duree_mois) || 180,
        revenu_mensuel: parseFloat(formData.revenu_mensuel) || 0
      };
      
      const response = await axios.post(`${API_URL}/api/coppam/simulations`, payload);
      setSimulationResult(response.data);
      setStep(3);
      toast.success('Simulation effectuée avec succès');
    } catch (error) {
      const errorMsg = error.response?.data?.detail?.messages?.join(', ') || 
                       error.response?.data?.detail || 
                       'Erreur lors de la simulation';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const resetSimulation = () => {
    setSimulationResult(null);
    setStep(1);
    setFormData({
      type_acteur: 'particulier',
      secteur: 'habitat',
      montant_projet: '',
      apport_personnel: '',
      duree_mois: '180',
      revenu_mensuel: ''
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'eligible':
        return <Badge className="bg-green-500 text-white font-semibold">✅ Éligible</Badge>;
      case 'eligible_avec_conditions':
        return <Badge className="bg-amber-500 text-white font-semibold">⚠️ Éligible avec conditions</Badge>;
      case 'non_eligible':
        return <Badge className="bg-red-500 text-white font-semibold">❌ Non éligible</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BackButton />
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Shield className="h-6 w-6" />
                  COPPAM Garanties Solidaires
                </h1>
                <p className="text-blue-200 text-sm">Espace Adhérent - Simulateur Multi-Territoires DOM</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-white text-slate-800/10 text-white border-white/30 font-semibold">
                <Globe className="h-3 w-3 mr-1" />
                7 Territoires DOM
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Stepper */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-4">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                  step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s === 1 && <MapPin className="h-5 w-5" />}
                  {s === 2 && <Calculator className="h-5 w-5" />}
                  {s === 3 && <FileText className="h-5 w-5" />}
                </div>
                {s < 3 && <ArrowRight className={`h-5 w-5 ${step > s ? 'text-blue-600' : 'text-gray-300'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step 1: Choix Territoire */}
        {step === 1 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  Choisissez votre territoire
                </CardTitle>
                <CardDescription>
                  Sélectionnez le territoire DOM pour votre projet solidaire
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {territories.map((territory) => (
                    <button
                      key={territory.code}
                      onClick={() => handleTerritorySelect(territory.code)}
                      className={`p-4 rounded-xl border-2 transition-all hover:shadow-lg ${
                        selectedTerritory === territory.code
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-3xl mb-2">
                          {territory.code === 'GP' && '🇬🇵'}
                          {territory.code === 'MQ' && '🇲🇶'}
                          {territory.code === 'GF' && '🇬🇫'}
                          {territory.code === 'RE' && '🇷🇪'}
                          {territory.code === 'YT' && '🇾🇹'}
                          {territory.code === 'MF' && '🇲🇫'}
                          {territory.code === 'BL' && '🇧🇱'}
                        </div>
                        <div className="font-semibold text-gray-800">{territory.name}</div>
                        <div className="text-xs text-gray-500">{territory.region}</div>
                        <Badge variant="outline" className="mt-2 text-xs">
                          CST: {territory.cst}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Détails territoire sélectionné */}
                {territoryDetails && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <Info className="h-4 w-4" />
                      Paramètres {territoryDetails.name}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Coefficient CST</span>
                        <div className="font-semibold">{territoryDetails.coefficients.cst}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Coefficient CP-T</span>
                        <div className="font-semibold">{territoryDetails.coefficients.cp_t}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Cadre juridique</span>
                        <div className="font-semibold">{territoryDetails.parameters.civil_code}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Risques majeurs</span>
                        <div className="flex flex-wrap gap-1">
                          {territoryDetails.risks.map((risk, i) => (
                            <Badge key={i} variant="outline" className="text-xs capitalize">{risk}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex justify-end">
                  <Button 
                    onClick={() => setStep(2)} 
                    disabled={!selectedTerritory}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Continuer <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Formulaire Simulation */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-blue-600" />
                Simulation de contribution solidaire
              </CardTitle>
              <CardDescription>
                Territoire: <strong>{territories.find(t => t.code === selectedTerritory)?.name}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitSimulation} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Type d'acteur */}
                  <div className="space-y-2">
                    <Label>Type d'acteur</Label>
                    <Select 
                      value={formData.type_acteur} 
                      onValueChange={(v) => setFormData({...formData, type_acteur: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="particulier">Particulier</SelectItem>
                        <SelectItem value="tpe">TPE / Auto-entrepreneur</SelectItem>
                        <SelectItem value="association">Association</SelectItem>
                        <SelectItem value="exploitant">Exploitant agricole</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Secteur */}
                  <div className="space-y-2">
                    <Label>Secteur d'activité</Label>
                    <Select 
                      value={formData.secteur} 
                      onValueChange={(v) => setFormData({...formData, secteur: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="habitat">Habitat / Résidence</SelectItem>
                        <SelectItem value="btp">BTP / Construction</SelectItem>
                        <SelectItem value="agriculture">Agriculture</SelectItem>
                        <SelectItem value="commerce">Commerce</SelectItem>
                        <SelectItem value="formation">Formation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Montant projet */}
                  <div className="space-y-2">
                    <Label>Montant du projet (€) *</Label>
                    <Input
                      type="number"
                      value={formData.montant_projet}
                      onChange={(e) => setFormData({...formData, montant_projet: e.target.value})}
                      placeholder="150000"
                      required
                      min="1000"
                      max="500000"
                    />
                  </div>

                  {/* Apport personnel */}
                  <div className="space-y-2">
                    <Label>Apport personnel (€)</Label>
                    <Input
                      type="number"
                      value={formData.apport_personnel}
                      onChange={(e) => setFormData({...formData, apport_personnel: e.target.value})}
                      placeholder="30000"
                      min="0"
                    />
                  </div>

                  {/* Durée */}
                  <div className="space-y-2">
                    <Label>Durée (mois)</Label>
                    <Select 
                      value={formData.duree_mois} 
                      onValueChange={(v) => setFormData({...formData, duree_mois: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="60">5 ans (60 mois)</SelectItem>
                        <SelectItem value="120">10 ans (120 mois)</SelectItem>
                        <SelectItem value="180">15 ans (180 mois)</SelectItem>
                        <SelectItem value="240">20 ans (240 mois)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Revenu mensuel */}
                  <div className="space-y-2">
                    <Label>Revenu mensuel / CA mensuel (€)</Label>
                    <Input
                      type="number"
                      value={formData.revenu_mensuel}
                      onChange={(e) => setFormData({...formData, revenu_mensuel: e.target.value})}
                      placeholder="2500"
                      min="0"
                    />
                    <p className="text-xs text-gray-500">Pour calculer le taux d'effort</p>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button type="button" variant="outline" onClick={() => setStep(1)}>
                    Retour
                  </Button>
                  <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                    {loading ? 'Calcul en cours...' : 'Calculer ma contribution'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Résultat */}
        {step === 3 && simulationResult && (
          <div className="space-y-6">
            {/* Résumé */}
            <Card className={`border-2 ${
              simulationResult.status === 'eligible' ? 'border-green-500 bg-green-50/50' :
              simulationResult.status === 'eligible_avec_conditions' ? 'border-amber-500 bg-amber-50/50' :
              'border-red-500 bg-red-50/50'
            }`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Résultat de votre simulation
                  </CardTitle>
                  {getStatusBadge(simulationResult.status)}
                </div>
                <CardDescription>
                  ID: {simulationResult.simulation_id}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center p-4 bg-white rounded-xl shadow">
                    <div className="text-3xl font-bold text-blue-600">
                      {simulationResult.contribution.contribution_mensuelle.toFixed(2)} €
                    </div>
                    <div className="text-sm text-gray-500">Contribution mensuelle</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl shadow">
                    <div className="text-3xl font-bold text-green-600">
                      {simulationResult.contribution.contribution_totale.toFixed(2)} €
                    </div>
                    <div className="text-sm text-gray-500">Contribution totale</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl shadow">
                    <div className="text-3xl font-bold text-purple-600">
                      {simulationResult.contribution.taux_effort.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-500">Taux d'effort</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl shadow">
                    <div className="text-3xl font-bold text-amber-600">
                      {simulationResult.contribution.credits_requis}
                    </div>
                    <div className="text-sm text-gray-500">Crédits solidaires</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Répartition */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  Répartition de votre contribution solidaire
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold">Prévention</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-600">
                      {simulationResult.contribution.part_prevention.toFixed(2)} €
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Actions de prévention et formation
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="h-5 w-5 text-green-600" />
                      <span className="font-semibold">Entraide mutuelle</span>
                    </div>
                    <div className="text-2xl font-bold text-green-600">
                      {simulationResult.contribution.part_entraide.toFixed(2)} €
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Solidarité entre adhérents
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Wallet className="h-5 w-5 text-purple-600" />
                      <span className="font-semibold">Fonds FOGEDOM</span>
                    </div>
                    <div className="text-2xl font-bold text-purple-600">
                      {simulationResult.contribution.part_fonds_solidaire.toFixed(2)} €
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Fonds de solidarité territorial
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Messages & Validations */}
            {(simulationResult.messages.length > 0 || simulationResult.validations_requises.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="h-5 w-5 text-amber-600" />
                    Informations importantes
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {simulationResult.messages.map((msg, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{msg}</span>
                    </div>
                  ))}
                  {simulationResult.validations_requises.map((val, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-amber-700">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="capitalize">{val.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Mention légale */}
            <div className="p-4 bg-gray-100 rounded-xl border border-gray-200 text-sm text-gray-600">
              <div className="flex items-start gap-2">
                <Shield className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Mention légale ESS</strong>
                  <p className="mt-1 whitespace-pre-line">{LEGAL_NOTICE}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between">
              <Button variant="outline" onClick={resetSimulation}>
                Nouvelle simulation
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Télécharger PDF
                </Button>
                <Button className="bg-green-600 hover:bg-green-700">
                  Adhérer maintenant
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
